import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Mic, ArrowUp, RotateCw } from "lucide-react";
import Orb from "@/components/Orb";
import Markdown from "@/components/Markdown";
import DraftCard from "@/components/DraftCard";
import Meetings from "@/pages/Meetings";
import { api, streamMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MsReconnectBanner from "@/components/MsReconnectBanner";

const SUGGESTIONS = [
  "Brief my day",
  "Draft urgent replies",
  "Prep my next meeting",
  "A verse for today",
];

function greeting(name) {
  const h = new Date().getHours();
  const greet =
    h < 5 ? "Up early" :
    h < 12 ? "Good morning" :
    h < 18 ? "Good afternoon" : "Good evening";
  return `${greet}, ${name?.split(" ")[0] || "friend"}.`;
}

export default function Chat() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [convCategory, setConvCategory] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamingId, setStreamingId] = useState(null);
  const [text, setText] = useState("");
  const composerRef = useRef(null);
  const scrollRef = useRef(null);

  // Load conversation
  useEffect(() => {
    if (!cid) {
      const id = setTimeout(() => setMessages([]), 0);
      return () => clearTimeout(id);
    }
    api.get(`/conversations/${cid}`).then((r) => {
      setMessages(r.data.messages || []);
    }).catch(() => setMessages([]));
  }, [cid]);

  // Keep the visible chat in sync when a voice turn posts to this conversation
  useEffect(() => {
    const onUpd = (e) => {
      if (e.detail?.cid && e.detail.cid === cid) {
        api.get(`/conversations/${cid}`).then((r) => setMessages(r.data.messages || [])).catch(() => {});
      }
    };
    window.addEventListener("disciple:conv-updated", onUpd);
    return () => window.removeEventListener("disciple:conv-updated", onUpd);
  }, [cid]);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  // Prefill the composer from an onboarding starter prompt passed via router state.
  // Works whether Chat is freshly mounted or already mounted (same-route navigate).
  useEffect(() => {
    const starter = location.state?.starter;
    if (!starter) return undefined;
    const id = setTimeout(() => {
      setText(starter);
      composerRef.current?.focus();
      navigate(location.pathname, { replace: true, state: {} });
    }, 50);
    return () => clearTimeout(id);
  }, [location.key]);

  const startConversationAndSend = async (msg) => {
    let conversationId = cid;
    if (!conversationId) {
      const r = await api.post("/conversations");
      conversationId = r.data.id;
      navigate(`/chat/${conversationId}`, { replace: true });
    }
    await sendToConversation(conversationId, msg);
  };

  const sendToConversation = async (conversationId, msg) => {
    setStreaming(true);
    setStreamingText("");
    // Optimistic user message
    setMessages((prev) => [...prev, {
      id: `local_${Date.now()}`, role: "user", content: msg, drafts: [], created_at: new Date().toISOString(),
    }]);
    try {
      let assistantId = null;
      let fullText = "";
      for await (const ev of streamMessage(conversationId, msg)) {
        if (ev.type === "meta") {
          assistantId = ev.data.assistant_id;
          setStreamingId(assistantId);
        } else if (ev.type === "delta") {
          fullText += ev.data.t;
          setStreamingText(fullText);
        } else if (ev.type === "done") {
          // Replace streaming with final message (with drafts)
          setMessages((prev) => [...prev, ev.data.message]);
          setStreamingText("");
          setStreamingId(null);
        } else if (ev.type === "error") {
          toast.error("Stream error: " + ev.data.error);
        }
      }
    } catch (e) {
      toast.error("Couldn't reach Andre.");
      setStreamingText("");
    } finally {
      setStreaming(false);
    }
  };

  const onSend = (override) => {
    const msg = (override ?? text).trim();
    if (!msg || streaming) return;
    setText("");
    startConversationAndSend(msg);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const onChipClick = (c) => {
    onSend(c);
  };

  const onDraftUpdate = (draftId, status, fields) => {
    setMessages((prev) => prev.map((m) => ({
      ...m,
      drafts: (m.drafts || []).map((d) =>
        d.id === draftId ? { ...d, status, ...(fields ? { fields } : {}) } : d
      ),
    })));
  };

  const empty = messages.length === 0 && !streaming;

  const [tab, setTab] = useState("conversation"); // conversation | meetings | actions
  const [openActions, setOpenActions] = useState([]);

  useEffect(() => {
    if (tab === "actions") {
      api.get("/actions/open").then((r) => setOpenActions(r.data)).catch(() => {});
    }
  }, [tab]);

  const newConversation = async () => {
    const r = await api.post("/conversations");
    navigate(`/chat/${r.data.id}`);
  };

  return (
    <>
      <div className="surface-header" style={{ paddingBottom: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <ChatTab label="Conversation" active={tab === "conversation"} onClick={() => setTab("conversation")} testid="chat-tab-conversation" />
          <ChatTab label="Meetings" active={tab === "meetings"} onClick={() => setTab("meetings")} testid="chat-tab-meetings" />
          <ChatTab label="Actions" active={tab === "actions"} onClick={() => setTab("actions")} testid="chat-tab-actions" />
        </div>
        <div className="status-pill" data-testid="connection-status">
          <span className="dot" /> {user?.assistant_name || "Andre"} · connected
        </div>
      </div>

      <MsReconnectBanner assistantName={user?.assistant_name || "Andre"} />

      {tab === "meetings" ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }} data-testid="embedded-meetings">
          <Meetings />
        </div>
      ) : tab === "actions" ? (
        <div style={{ flex: 1, overflowY: "auto", padding: "20px clamp(14px, 4vw, 32px) 60px" }} data-testid="open-actions-flat">
          <div style={{ maxWidth: 820, margin: "0 auto" }}>
            <div className="text-dim" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
              {openActions.length} open action items across all meetings
            </div>
            {openActions.length === 0 && <div className="text-dim">All caught up.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {openActions.map((a) => (
                <div
                  key={`${a.meeting_id}-${a.idx}`}
                  onClick={() => navigate(`/meetings/${a.meeting_id}`)}
                  className="card-row"
                  style={{ borderLeft: `3px solid ${a.meeting_color || "#6B5CE7"}`, cursor: "pointer" }}
                  data-testid={`chat-action-${a.meeting_id}-${a.idx}`}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: "var(--ink)" }}>
                      {a.owner && <strong>{a.owner}</strong>}{a.owner ? " — " : ""}{a.text}
                    </div>
                    <div className="text-dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                      {a.meeting_title}{a.due ? ` · due ${a.due}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "16px clamp(14px, 4vw, 32px) 0" }} data-testid="chat-scroll">
        {empty ? (
          <EmptyState user={user} onChip={onChipClick} />
        ) : (
          <div style={{ maxWidth: 780, margin: "0 auto", paddingBottom: 24 }}>
            {messages.map((m) => (
              <MessageRow key={m.id} m={m} user={user} cid={cid} onDraftUpdate={onDraftUpdate} convCategory={convCategory} />
            ))}
            {streaming && streamingText && (
              <div className="message-row fade-up">
                <Orb size={32} state="thinking" />
                <div style={{ flex: 1 }}>
                  <div className="message-name" style={{ marginBottom: 6 }}>{user?.assistant_name || "Andre"}</div>
                  <div style={{ borderLeft: "2px solid var(--cobalt-bright)", paddingLeft: 14 }}>
                    <Markdown text={streamingText} />
                  </div>
                </div>
              </div>
            )}
            {streaming && !streamingText && (
              <div className="message-row fade-up">
                <Orb size={32} state="thinking" />
                <div style={{ flex: 1 }}>
                  <div className="message-name" style={{ marginBottom: 6 }}>{user?.assistant_name || "Andre"}</div>
                  <div className="text-dim" style={{ fontSize: 13 }}>Thinking…</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="composer-wrap">
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div className="composer">
            <button
              onClick={newConversation}
              className="composer-btn"
              data-testid="new-conv-btn"
              title="New conversation"
              style={{ width: "auto", padding: "0 14px", gap: 6, fontSize: 13, color: "var(--ink-soft)", fontWeight: 500 }}
            >
              <RotateCw size={15} strokeWidth={2} /> New
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("disciple:wake", { detail: { phrase: "(mic)" } }))}
              className="composer-btn"
              data-testid="mic-btn"
              title="Voice mode"
            >
              <Mic size={18} strokeWidth={2} />
            </button>
            <textarea
              ref={composerRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Ask ${user?.assistant_name || "Andre"} anything…`}
              data-testid="composer-input"
              rows={1}
            />
            <button
              onClick={() => onSend()}
              className="composer-btn composer-send"
              data-testid="send-btn"
              disabled={!text.trim() || streaming}
              style={{ opacity: !text.trim() || streaming ? 0.5 : 1, width: "auto", padding: "0 18px", gap: 6, fontSize: 14, fontWeight: 600 }}
              title="Send"
            >
              Send <ArrowUp size={18} strokeWidth={2} />
            </button>
          </div>
          <div className="reassurance" data-testid="reassurance">
            <span className="dot" />
            {user?.assistant_name || "Andre"} drafts and proposes — never sends or acts without your confirmation.
          </div>
        </div>
      </div>
      </>
      )}
    </>
  );
}

function EmptyState({ user, onChip }) {
  return (
    <div className="fade-up" style={{
      maxWidth: 640, margin: "0 auto",
      display: "flex", flexDirection: "column", alignItems: "center",
      paddingTop: "12vh", paddingBottom: 40, textAlign: "center",
    }}>
      <Orb size={104} state="listening" />
      <h1 style={{ marginTop: 32, fontSize: 56, lineHeight: "56px" }} data-testid="empty-greeting">
        {greeting(user?.name)}
      </h1>
      <div className="text-soft" style={{ marginTop: 14, maxWidth: 480, fontSize: 16, lineHeight: 1.6 }}>
        I’m here. What would you like to think through?
      </div>
      <div style={{ marginTop: 30, display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }} data-testid="suggestion-chips">
        {SUGGESTIONS.map((s, i) => (
          <button key={s} className="chip" onClick={() => onChip(s)} data-testid={`chip-${i}`}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatTab({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        background: "transparent",
        border: "none",
        padding: "12px 4px",
        cursor: "pointer",
        fontSize: 14,
        color: active ? "var(--ink)" : "var(--ink-dim)",
        borderBottom: active ? "2px solid var(--ph-product-teal)" : "2px solid transparent",
        fontWeight: active ? 600 : 500,
        marginBottom: -1,
      }}
    >
      {label}
    </button>
  );
}

function MessageRow({ m, user, cid, onDraftUpdate, convCategory }) {
  const navigate = useNavigate();
  const isUser = m.role === "user";
  const initials = isUser
    ? (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()
    : null;
  const goSource = (s) => {
    if (s.kind === "vault") navigate(`/vault/${s.id}`);
    else if (s.kind === "meeting") navigate(`/meetings/${s.id}`);
    else if (s.kind === "person") navigate(`/people`);
  };
  const SOURCE_STYLE = {
    vault:   { bg: "rgba(107,92,231,0.12)", bd: "rgba(107,92,231,0.45)", fg: "#A99BF1", label: "Vault"   },
    meeting: { bg: "rgba(123,111,232,0.12)", bd: "rgba(123,111,232,0.45)", fg: "#AEA3F1", label: "Meeting" },
    person:  { bg: "rgba(78,205,196,0.12)", bd: "rgba(78,205,196,0.45)", fg: "#7EE0D5", label: "Person"  },
  };
  const time = (() => {
    try { return new Date(m.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  })();
  const category = isUser ? convCategory : null;
  /* Brand palette only: Brand Violet, Brand Teal, Accent Purple, Warm Gold, PH Magenta. */
  const CAT_COLOR = {
    "Topic / Strategy": "#6B5CE7", "Email": "#4ECDC4", "Meeting": "#7B6FE8",
    "Document": "#F2B65A", "Person": "#F25C8E",
  };
  const catColor = CAT_COLOR[category] || "#6B5CE7";
  const catShort = category ? (category.split(" ")[0]) : "";
  const sourceCount = (m.sources || []).length;
  const borderColor = sourceCount > 0 ? "var(--ph-product-teal)" : "var(--ph-accent-purple)";

  return (
    <div className="message-row fade-up" data-testid={`message-${m.role}`}>
      {isUser ? (
        <div className="avatar-circle" style={{ background: "var(--panel-3)", width: 32, height: 32, color: "var(--ink-soft)" }}>
          {initials}
        </div>
      ) : (
        <Orb size={32} state="idle" />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div className="message-name">{isUser ? (user?.name || "You") : (user?.assistant_name || "Andre")}</div>
          {time && <span className="text-dim" style={{ fontSize: 10.5, letterSpacing: "0.04em" }}>{time}</span>}
          {category && (
            <span style={{
              fontSize: 9.5, padding: "2px 8px", borderRadius: 999,
              background: `${catColor}1A`, color: catColor,
              border: `1px solid ${catColor}59`,
              letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
              <span style={{ width: 5, height: 5, borderRadius: 50, background: catColor, display: "inline-block" }} />
              {catShort}
            </span>
          )}
        </div>
        <div style={isUser ? {} : { borderLeft: `2px solid ${borderColor}`, paddingLeft: 14 }}>
          <Markdown text={m.content} />
          {(m.drafts || []).map((d) => (
            <DraftCard key={d.id} conversationId={cid} draft={d} onUpdate={onDraftUpdate} />
          ))}
          {!isUser && sourceCount > 0 && (
            <div data-testid={`sources-${m.id}`} style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <span
                style={{
                  display: "inline-flex", alignItems: "center", alignSelf: "flex-start",
                  fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                  color: "var(--ph-product-teal)", fontWeight: 700,
                  padding: "3px 9px", borderRadius: 4,
                  border: "1px solid rgba(76,214,190,0.45)",
                  background: "rgba(76,214,190,0.10)",
                }}
                data-testid={`tools-badge-${m.id}`}
              >
                {sourceCount} {sourceCount === 1 ? "tool" : "tools"}
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {m.sources.map((s) => {
                  const st = SOURCE_STYLE[s.kind] || SOURCE_STYLE.vault;
                  return (
                    <button
                      key={`${s.kind}-${s.id}`}
                      onClick={() => goSource(s)}
                      data-testid={`source-chip-${s.id}`}
                      title={`Open ${s.label}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "4px 10px", borderRadius: 999,
                        background: st.bg, border: `1px solid ${st.bd}`,
                        color: st.fg, fontSize: 11.5, cursor: "pointer", maxWidth: 320,
                      }}
                    >
                      <span style={{ fontSize: 9.5, letterSpacing: "0.12em", fontWeight: 700, opacity: 0.85 }}>
                        {st.label.toUpperCase()}
                      </span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
